import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { IconEnvelopeCloseOutline, SlideIn } from '@/components';
import {
  type CandidateBasicDto,
  type InviteCandidateResponse,
  SendInvitationChannel,
} from '@/services/api';

import { CircularIconContainer } from '../../components/Icons/IconContainer';
import {
  useCandidatesBulkInviteMutation,
  useInviteAllCandidatesRequest,
} from './useCandidateTableMutation';

interface WhatsappCandidatesConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  interviewId?: string;
  selectedCandidates: Array<CandidateBasicDto>;
}

export const WhatsappCandidatesConfirmationModal: React.FC<
  WhatsappCandidatesConfirmationModalProps
> = ({ isOpen, onClose, interviewId, selectedCandidates }) => {
  const toast = useToast();
  const { t } = useTranslation(['common', 'candidate']);

  const count = selectedCandidates.length;

  const candidatesAdd = useCandidatesBulkInviteMutation({
    onSuccess() {
      onClose();
    },
  });

  const inviteAllCandidates = useInviteAllCandidatesRequest({
    onError() {},
    onSuccess() {
      onClose();
    },
  });

  const bulkInviteHandler = async () => {
    let mutationPromise: Promise<InviteCandidateResponse>;
    if (interviewId) {
      mutationPromise = candidatesAdd.mutateAsync({
        candidateIds: selectedCandidates.map((c) => c.id),
        interviewId: interviewId,
        channel: SendInvitationChannel.Whatsapp,
      });
    } else {
      mutationPromise = inviteAllCandidates.mutateAsync({
        body: {
          candidateIds: selectedCandidates.map((c) => c.id),
          channel: SendInvitationChannel.Whatsapp,
        },
      });
    }

    toast.promise(mutationPromise, {
      loading: {
        title: t('candidate:inviteNewCandidate.whatsappSendingNotification', {
          count: selectedCandidates.length,
        }),
      },
      success: (response) => {
        const notInvitedCount = response.notInvitedCount ?? 0;
        const totalCount = response.totalCount ?? 0;

        if (notInvitedCount === 0) {
          return {
            title: t('candidate:inviteNewCandidate.whatsappSentNotification', {
              count: selectedCandidates.length,
            }),
          };
        }

        if (totalCount === 1 && notInvitedCount === 1) {
          return {
            title: t(
              'candidate:inviteNewCandidate.failSentNotificationForOneCandidate'
            ),
            status: 'warning',
          };
        }

        return {
          title: t(
            'candidate:inviteNewCandidate.failSentNotificationForCandidates'
          ),
          status: 'warning',
        };
      },
      error: {
        title: t('candidate:inviteNewCandidate.addCandidateError'),
      },
    });
  };

  return (
    <Modal
      isCentered
      size="md"
      isOpen={isOpen}
      onClose={() => {
        onClose();
      }}
    >
      <ModalOverlay />
      <ModalContent borderRadius="xl" pt={6} alignItems="center">
        <ModalCloseButton
          borderRadius="full"
          borderWidth="1px"
          borderColor="gray.100"
        />

        <VStack gap={0} w="100%" px={6}>
          <ModalHeader justifyItems="center" px={0}>
            <Flex justifyContent="center" mb={3}>
              <CircularIconContainer icon={IconEnvelopeCloseOutline} />
            </Flex>
            <Text mb={1} textAlign="center" fontSize="xl" fontWeight="semibold">
              {t('actions.sendWhatsapp')}
            </Text>
            {/* <Text
              textAlign="center"
              fontSize="sm"
              fontWeight="semibold"
              color="gray.600"
            >
              {t('sms.selectTemplate')}
            </Text> */}
          </ModalHeader>

          <ModalBody w="full" pb={2} px={0}>
            <SlideIn>
              <Button
                onClick={bulkInviteHandler}
                mb={4}
                mt={6}
                isLoading={candidatesAdd.isPending}
                variant="@primary"
                w="full"
              >
                {t('candidate:inviteNewCandidate.whatsappToCandidates', {
                  count,
                })}
              </Button>

              {/* <Link to="/admin/settings/sms" mb={5}>
                <Button
                  rightIcon={<RiArrowRightSLine size="20px" />}
                  variant="link"
                  color="primary.500"
                  w="full"
                >
                  {t('sms.smsSettings')}
                </Button>
              </Link> */}
            </SlideIn>
          </ModalBody>
        </VStack>
      </ModalContent>
    </Modal>
  );
};
